/**
 * MLBSidebar — Persistent left navigation for MLB team dashboards.
 *
 * Ported from `components/coaching/CoachingSidebar.jsx` per the MLB dashboard
 * design spec (MLB_TEAM_DASHBOARD_DESIGN.md §5.1 "Navigation shell"):
 *   - 240px fixed width, 64px collapsed
 *   - Active item uses `var(--team-primary)` instead of the coaching
 *     sidebar's gold — theme payoff from Phase 1B's TeamThemeProvider.
 *
 * NAV_SECTIONS only lists items whose `/mlb/<slug>` routes are actually
 * wired up in `App.js`. The spec's full 8-section / 24-item IA lives in
 * MLB_TEAM_DASHBOARD_DESIGN.md §5.1 as the long-term target; items get
 * added back here the same commit that adds their route + page. Previous
 * revisions shipped the whole IA, which routed every unmapped link back
 * to /mlb/overview through the App `*` catch-all and looked to users
 * like every nav click was broken.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  ClipboardList,
  Sparkles,
  Binoculars,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'CLUB',
    items: [
      { id: 'overview', label: 'Overview', icon: Home, path: '/mlb/overview' },
    ],
  },
  {
    label: 'PLAYERS',
    items: [
      { id: 'roster', label: 'Roster & Evaluations', icon: ClipboardList, path: '/mlb/roster' },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      { id: 'ask-ai', label: 'Ask SLAM AI (full-page)', icon: Sparkles, path: '/mlb/ask-ai' },
      { id: 'advance-scouting', label: 'Advance Scouting', icon: Binoculars, path: '/mlb/advance-scouting' },
    ],
  },
];

/**
 * Sidebar section label style — per design spec §5.1:
 *   0.6875rem / 700 / 0.14em tracking / rgba(223, 226, 235, 0.72)
 * Ported verbatim from CoachingSidebar so the label typography rhythm
 * stays identical across coaching and MLB surfaces.
 */
const sectionHeadingStyle = {
  padding: '0 20px 8px',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'rgba(223, 226, 235, 0.72)',
  fontFamily: 'var(--font-family-headline)',
};

/**
 * Active-item background uses the team-primary color composited at 8% opacity.
 * `--team-primary-rgb` is kept in lockstep with `--team-primary` in
 * design-tokens.css so every [data-team] override swaps both together.
 */
const ACTIVE_BG = 'rgba(var(--team-primary-rgb), 0.08)';

/**
 * MLBSidebar
 *
 * @param {Object} props
 * @param {boolean} [props.collapsed=false]   Render 64px rail vs 240px full sidebar.
 * @param {() => void} [props.onToggleCollapse]  Collapse-toggle handler.
 * @param {boolean} [props.isMobile=false]    Drops z-index down so mobile drawers layer correctly.
 * @param {() => void} [props.onNavItemClick] Fires after any nav-item click — used by mobile drawer to close.
 */
export default function MLBSidebar({
  collapsed = false,
  onToggleCollapse,
  isMobile = false,
  onNavItemClick,
}) {
  const handleItemClick = () => {
    if (onNavItemClick) onNavItemClick();
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.id}
        to={item.path}
        title={collapsed ? item.label : undefined}
        onClick={handleItemClick}
        style={({ isActive }) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? '0' : '10px',
          width: '100%',
          padding: collapsed ? '8px 0' : '8px 20px',
          textDecoration: 'none',
          background: isActive ? ACTIVE_BG : 'transparent',
          borderLeft: isActive
            ? '2px solid var(--team-primary)'
            : '2px solid transparent',
          color: isActive
            ? 'var(--team-primary)'
            : 'var(--text-secondary)',
          fontSize: '0.8125rem',
          fontWeight: 500,
          fontFamily: 'var(--font-family-body)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 150ms ease',
          boxSizing: 'border-box',
        })}
        onMouseEnter={(e) => {
          // NavLink's active state is driven by react-router, not local state,
          // so we key hover behavior off the same aria-current attribute it
          // sets on the matched link.
          if (e.currentTarget.getAttribute('aria-current') !== 'page') {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget.getAttribute('aria-current') !== 'page') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }
        }}
      >
        <Icon size={16} strokeWidth={1.5} />
        {!collapsed && item.label}
      </NavLink>
    );
  };

  return (
    <nav
      data-testid="mlb-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: collapsed ? '64px' : 'var(--sidebar-width)',
        height: '100vh',
        background: 'var(--bg-surface-0)',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: isMobile ? 50 : 'var(--z-fixed)',
        transition: 'width 250ms ease',
        boxSizing: 'border-box',
      }}
    >
      {/* Collapse toggle — header/brand block intentionally deferred to
          Phase 3 per the MLB design spec §5.2 (40x40 brand tile lives in
          the main content header, not inside the sidebar). */}
      <button
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: '6px 0',
          background: 'transparent',
          border: 'none',
          color: 'var(--text-quaternary)',
          cursor: 'pointer',
          marginBottom: '12px',
          transition: 'color 150ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-quaternary)'; }}
      >
        {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
      </button>

      {/* Navigation sections */}
      <div data-guide="sidebar-nav" style={{ flex: 1 }}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} style={{ marginBottom: '20px' }}>
            {!collapsed && (
              <div style={sectionHeadingStyle}>{section.label}</div>
            )}
            {section.items.map(renderNavItem)}
          </div>
        ))}
      </div>
    </nav>
  );
}

export { NAV_SECTIONS };
